import { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import {
  fetchGanttData,
  fetchProjectMeasures,
  saveGanttStructure,
  reCalculatePhasedEem,
  getProjectIdFromQuery,
} from "../services/heliosApi";
import { transformEemToTasks } from "../utils/transformEemData";
import { transformToBackendFormat, parseDateLocal } from "../utils/transformGanttData";
import { buildDeerMeta } from "../utils/deerMappings";
import { useEemData } from "../context/EemDataContext";
import "./HeliosPlanner.css";

// ---------------------------------------------------------------------------
// Custom Helios phase planner. Replaces wx-react-gantt with plain React + CSS:
// the data model stays the flat task list used by the transforms (phases have
// parent 0 + isPhase, measures parent to a phase id), so the whole Helios data
// layer (heliosApi / transformGanttData / transformEemData) is reused as-is.
// ---------------------------------------------------------------------------

const MIN_YEAR = 2025;
const MAX_YEAR = 2060;
const YEARS = Array.from({ length: MAX_YEAR - MIN_YEAR + 1 }, (_, i) => MIN_YEAR + i);
const COL_W = 92;
const LABEL_W = 300;
const DATE_W = 84;

const PHASE_COLORS = [
  { bg: "#2563EB", light: "#EFF6FF", border: "#BFDBFE", text: "#1E40AF" },
  { bg: "#7C3AED", light: "#F5F3FF", border: "#DDD6FE", text: "#5B21B6" },
  { bg: "#059669", light: "#ECFDF5", border: "#A7F3D0", text: "#065F46" },
  { bg: "#D97706", light: "#FFFBEB", border: "#FDE68A", text: "#92400E" },
  { bg: "#DC2626", light: "#FEF2F2", border: "#FECACA", text: "#991B1B" },
  { bg: "#0891B2", light: "#ECFEFF", border: "#A5F3FC", text: "#155E75" },
];

const yearOf = (d) => new Date(d).getFullYear();
const clampYear = (y) => Math.min(MAX_YEAR, Math.max(MIN_YEAR, y));
const jan1 = (y) => new Date(y, 0, 1);
const dec31 = (y) => new Date(y, 11, 31);
const isRootParent = (p) => p === 0 || p === "0" || p === null || p === undefined;

/*
 * Phase order follows the start year. The posted phaseOrder is the array position and the
 * simulator builds phase N on the retrofitted baseline of phases 1..N-1, so a phase moved to a
 * later year must also move down the list or it is simulated out of sequence. Every change to a
 * phase's year goes through here, and so does the save payload. Phases named by their position
 * ("Phase 3") are renumbered to the slot they land in; a phase with its own name keeps it, and so
 * does a year-named one from Auto-create ("Phase 2027"). Phases sharing a year keep their current
 * relative order (Array.sort is stable).
 */
const GENERIC_PHASE_NAME = /^Phase \d{1,3}$/i;

const orderPhasesByYear = (taskList) => {
  const phaseList = taskList.filter((t) => t.isPhase);
  const sorted = [...phaseList].sort((a, b) => yearOf(a.start) - yearOf(b.start));
  const phaseIds = new Set(phaseList.map((p) => p.id));
  const rebuilt = [];
  let changed = false;
  sorted.forEach((phase, index) => {
    if (phase.id !== phaseList[index].id) changed = true;
    const name = String(phase.text || "");
    const slotName = `Phase ${index + 1}`;
    const next = GENERIC_PHASE_NAME.test(name) && name !== slotName ? { ...phase, text: slotName } : phase;
    if (next !== phase) changed = true;
    rebuilt.push(next, ...taskList.filter((t) => !t.isPhase && t.parent === phase.id));
  });
  rebuilt.push(...taskList.filter((t) => !t.isPhase && (isRootParent(t.parent) || !phaseIds.has(t.parent))));
  return changed ? rebuilt : taskList;
};

const samePhaseOrder = (a, b) => {
  const ia = a.filter((t) => t.isPhase).map((t) => t.id);
  const ib = b.filter((t) => t.isPhase).map((t) => t.id);
  return ia.length === ib.length && ia.every((id, i) => id === ib[i]);
};

const getMeasureCategoryText = (m) => {
  const raw =
    m?.categoryLabel ??
    m?.originalMeasure?.doeCategory ??
    m?.originalMeasure?.ecmCategory ??
    m?.originalMeasure?.category ??
    m?.originalMeasure?.ecmCategoryId ??
    m?.doeCategory ??
    m?.ecmCategory ??
    m?.category ??
    m?.ecmCategoryId ??
    "";
  return String(raw).replace(/^Category:\s*/i, "").trim();
};

const getMeasureSubCategoryText = (m) => {
  const raw =
    m?.subCategoryLabel ??
    m?.originalMeasure?.doeSubCategory ??
    m?.originalMeasure?.ecmSubCategory ??
    m?.originalMeasure?.subCategory ??
    m?.originalMeasure?.ecmSubCategoryId ??
    m?.doeSubCategory ??
    m?.ecmSubCategory ??
    m?.subCategory ??
    m?.ecmSubCategoryId ??
    "";
  return String(raw).replace(/^Subcategory:\s*/i, "").trim();
};

// Engineering priority (1-3) and labor intensity (1-5) come from the reference measure, resolved
// server-side on the measures endpoint. Lower is first in both: priority 1 is the most urgent,
// labor intensity 1 the least disruptive, matching the order the simulator applies measures in.
// A measure with no reference rating stays null and is grouped as "Unrated" rather than guessed.
const getMeasureRating = (m, field) => {
  const raw = m?.[field] ?? m?.originalMeasure?.[field];
  if (raw === null || raw === undefined || raw === "") return null;
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
};

const UNRATED_KEY = "￿";

// Numeric measure economics. paybackAfterRebate is stored as a string, so both go through Number.
const getMeasureNumber = (m, field) => {
  const raw = m?.[field] ?? m?.originalMeasure?.[field];
  if (raw === null || raw === undefined || raw === "") return null;
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
};

// Marginal abatement cost, $/tCO2e. Negative means the measure pays for itself over its life,
// which is the split that actually matters when deciding what to do first.
const MAC_BUCKETS = [
  { value: "saving", label: "Cost saving (below 0)", test: (v) => v < 0 },
  { value: "0-50", label: "0 to 50", test: (v) => v >= 0 && v < 50 },
  { value: "50-100", label: "50 to 100", test: (v) => v >= 50 && v < 100 },
  { value: "100plus", label: "Above 100", test: (v) => v >= 100 },
];

// Simple payback in years. Negative means the measure never pays back, so it gets its own bucket
// rather than being swept in with fast paybacks by a naive "less than" test.
const PAYBACK_BUCKETS = [
  { value: "0-5", label: "Under 5 years", test: (v) => v >= 0 && v < 5 },
  { value: "5-10", label: "5 to 10 years", test: (v) => v >= 5 && v < 10 },
  { value: "10-20", label: "10 to 20 years", test: (v) => v >= 10 && v < 20 },
  { value: "20plus", label: "Over 20 years", test: (v) => v >= 20 },
  { value: "never", label: "Never pays back", test: (v) => v < 0 },
];

const bucketMatches = (buckets, selected, value) => {
  const bucket = buckets.find((b) => b.value === selected);
  return Boolean(bucket) && value !== null && bucket.test(value);
};

const ratingGroup = (m, field, labelPrefix) => {
  const value = getMeasureRating(m, field);
  return value === null
    ? { key: UNRATED_KEY, label: "Unrated" }
    : { key: String(value).padStart(3, "0"), label: `${labelPrefix} ${value}` };
};

const measureSearchBlob = (m) =>
  [m?.text, m?.details, m?.barDescription, m?.barMeta, m?.categoryLabel, m?.subCategoryLabel]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

// Inline line-icon set (replaces emoji glyphs so the toolbar reads as one
// consistent iconography instead of mixed platform emoji).
const HP_ICONS = {
  add: ["M12 5v14", "M5 12h14"],
  delete: ["M3 6h18", "M8 6V4h8v2", "M19 6l-1 14H6L5 6", "M10 11v6", "M14 11v6"],
  edit: ["M12 20h9", "M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4Z"],
  indent: ["M3 6h18", "M8 12h13", "M8 18h13", "M4 10l3 2-3 2Z"],
  up: ["M18 15l-6-6-6 6"],
  down: ["M6 9l6 6 6-6"],
  reorder: ["M7 4v16", "M7 4l-3 3", "M7 4l3 3", "M17 20V4", "M17 20l-3-3", "M17 20l3-3"],
  filters: ["M4 6h16", "M7 12h10", "M10 18h4"],
  refresh: ["M21 12a9 9 0 11-3-6.7", "M21 4v4h-4"],
  sparkle: ["M12 3l1.6 4.4L18 9l-4.4 1.6L12 15l-1.6-4.4L6 9l4.4-1.6L12 3z",
            "M19 14l.6 1.7L21.3 16l-1.7.6L19 18.3l-.6-1.7L16.7 16l1.7-.4L19 14z"],
  save: ["M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z", "M17 21v-8H7v8", "M7 3v5h8"],
};

function HpIcon({ name, className }) {
  const paths = HP_ICONS[name] || [];
  return (
    <svg
      className={`hp-svg ${className || ""}`}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths.map((d, i) => <path key={i} d={d} />)}
    </svg>
  );
}

// Right-click menu for phase/measure rows. Actions map to the same handlers the
// toolbar uses; items shown depend on the row kind (measures cannot be edited
// or deleted, matching the toolbar's own guard rails).
function ContextMenu({ x, y, task, canMoveUp, canMoveDown, onEdit, onMove, onDelete, onClose }) {
  const ref = useRef(null);
  const [pos, setPos] = useState({ x, y });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    setPos({
      x: Math.min(x, window.innerWidth - w - 8),
      y: Math.min(y, window.innerHeight - h - 8),
    });
  }, [x, y]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    const onScroll = () => onClose();
    document.addEventListener("keydown", onKey, true);
    document.addEventListener("mousedown", onClose, true);
    document.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onClose);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      document.removeEventListener("mousedown", onClose, true);
      document.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onClose);
    };
  }, [onClose]);

  const isPhase = task.isPhase;

  return createPortal(
    <div
      ref={ref}
      className="hp-ctx"
      style={{ left: pos.x, top: pos.y }}
      onMouseDown={(e) => e.stopPropagation()}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div className="hp-ctx-label" title={String(task.text || "")}>{String(task.text || "")}</div>
      {isPhase && (
        <button className="hp-ctx-item" onClick={() => { onClose(); onEdit(); }}>
          <HpIcon name="edit" /> Edit
        </button>
      )}
      <button className="hp-ctx-item" disabled={!canMoveUp} onClick={() => { onClose(); onMove(-1); }}>
        <HpIcon name="up" /> Move up
      </button>
      <button className="hp-ctx-item" disabled={!canMoveDown} onClick={() => { onClose(); onMove(1); }}>
        <HpIcon name="down" /> Move down
      </button>
      {isPhase && (
        <>
          <div className="hp-ctx-sep" />
          <button className="hp-ctx-item danger" onClick={() => { onClose(); onDelete(); }}>
            <HpIcon name="delete" /> Delete
          </button>
        </>
      )}
    </div>,
    document.body
  );
}

function Highlight({ text, query }) {
  if (!query) return text;
  const q = query.toLowerCase();
  const lower = String(text).toLowerCase();
  const idx = lower.indexOf(q);
  if (idx < 0) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="hp-mark">{text.slice(idx, idx + query.length)}</mark>
      {text.slice(idx + query.length)}
    </>
  );
}

function DeleteModal({ phase, measureNames, onConfirm, onCancel }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [onCancel]);

  return createPortal(
    <div className="hp-modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="hp-modal">
        <div className="hp-modal-icon">🗑</div>
        <div className="hp-modal-title">Delete "{phase.text}"?</div>
        <div className="hp-modal-body">
          This will permanently remove the phase
          {measureNames.length > 0 && (
            <> and unassign its {measureNames.length} measure{measureNames.length === 1 ? "" : "s"}</>
          )}
          . This action cannot be undone.
        </div>
        <div className="hp-modal-actions">
          <button className="hp-btn" onClick={onCancel}>Cancel</button>
          <button className="hp-btn hp-btn-danger" onClick={onConfirm}>Delete Phase</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function PhaseEditor({ mode, draft, onChange, onSave, onCancel, onDelete }) {
  const nameRef = useRef(null);
  useEffect(() => {
    nameRef.current?.focus();
    nameRef.current?.select();
  }, []);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [onCancel]);

  const yearsFrom = (min) => YEARS.filter((y) => y >= min);

  return (
    <div className="hp-editor">
      <div className="hp-editor-head">
        <span className="hp-editor-title">{mode === "create" ? "New Phase" : "Edit Phase"}</span>
        <button className="hp-editor-close" onClick={onCancel} title="Close">✕</button>
      </div>
      <label className="hp-field">
        <span>Phase Name</span>
        <input
          ref={nameRef}
          value={draft.name}
          onChange={(e) => onChange({ ...draft, name: e.target.value })}
          placeholder="Phase name"
        />
      </label>
      <label className="hp-field">
        <span>Start year</span>
        <select
          value={draft.startYear}
          onChange={(e) => {
            const startYear = Number(e.target.value);
            onChange({ ...draft, startYear, endYear: Math.max(startYear, draft.endYear) });
          }}
        >
          {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </label>
      <label className="hp-field">
        <span>End year</span>
        <select
          value={draft.endYear}
          onChange={(e) => onChange({ ...draft, endYear: Number(e.target.value) })}
        >
          {yearsFrom(draft.startYear).map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </label>
      <div className="hp-editor-actions">
        {mode === "edit" && (
          <button className="hp-btn hp-btn-danger-ghost" onClick={onDelete}>Delete</button>
        )}
        <button className="hp-btn" onClick={onCancel}>Cancel</button>
        <button className="hp-btn hp-btn-primary" onClick={onSave} disabled={!draft.name.trim()}>
          Save
        </button>
      </div>
    </div>
  );
}

export default function HeliosPlanner() {
  const { eemTasks, setEemTasks, loading, setLoading } = useEemData();

  const [selectedId, setSelectedId] = useState(null);
  const [collapsed, setCollapsed] = useState({});
  const [search, setSearch] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedSubCategory, setSelectedSubCategory] = useState("");
  const [selectedMac, setSelectedMac] = useState("");
  const [selectedPayback, setSelectedPayback] = useState("");
  // Local edits are not persisted until Save, so the toolbar has to say so.
  const [dirty, setDirty] = useState(false);
  const [editor, setEditor] = useState(null); // {mode, phaseId, draft}
  const [deleteTarget, setDeleteTarget] = useState(null); // phase task
  const [phaseMenuOpen, setPhaseMenuOpen] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [ctxMenu, setCtxMenu] = useState(null); // {x, y, task}
  const [tip, setTip] = useState(null); // {text, x, y} for truncated-name tooltip
  const savedPhasesRef = useRef([]);
  const scrollRef = useRef(null);
  const searchInputRef = useRef(null);

  // ------------------------------------------------------------------ load
  // Reused after every write instead of reloading the page. A reload cannot work here: the
  // session token lives in memory only and the one-shot ?code= is scrubbed from the URL once
  // redeemed, so a reloaded page has no way to authenticate and lands on "session token missing".
  const loadData = useCallback(
    async () => {
      try {
        setLoading(true);
        const projectId = getProjectIdFromQuery();
        const [ganttData, measuresData] = await Promise.all([
          fetchGanttData(projectId).catch(() => null),
          fetchProjectMeasures(projectId, { limit: 100, start: 0, query: null, sort: null }),
        ]);

        const eemRecords = measuresData?.records || measuresData || [];
        const backendPhases = ganttData?.phases || [];
        // Kept so a save can deactivate phases that have since been regrouped or deleted away:
        // the backend has no delete path, an omitted phase simply stays active.
        savedPhasesRef.current = backendPhases;
        const backendMeasures = ganttData?.measures || [];

        let finalTasks = [];
        if (backendPhases.length > 0) {
          const sortedPhases = [...backendPhases].sort(
            (a, b) => (a.phaseOrder || 0) - (b.phaseOrder || 0)
          );
          const phaseTasks = sortedPhases.map((phase) => ({
            id: phase.id,
            text: phase.phaseName,
            start: parseDateLocal(phase.startDate),
            end: parseDateLocal(phase.endDate),
            progress: 0,
            parent: 0,
            type: "task",
            open: true,
            isPhase: true,
          }));
          const measureTasks = backendMeasures.map((measure, index) => {
            const originalMeasure = eemRecords.find((e) => e.id === measure.id) || measure;
            const description = String(
              measure.ecmDescription || originalMeasure.ecmDescription || "Measure"
            ).replace(/\s+/g, " ").trim();
            const categoryValue =
              originalMeasure?.doeCategory ?? measure?.doeCategory ??
              originalMeasure?.ecmCategory ?? originalMeasure?.category ??
              originalMeasure?.ecmCategoryId ?? measure?.ecmCategoryId;
            const subCategoryValue =
              originalMeasure?.doeSubCategory ?? measure?.doeSubCategory ??
              originalMeasure?.ecmSubCategory ?? originalMeasure?.subCategory ??
              originalMeasure?.ecmSubCategoryId ?? measure?.ecmSubCategoryId;
            const { categoryLabel, subCategoryLabel, barMeta } = buildDeerMeta(
              categoryValue, subCategoryValue
            );
            const parentPhase = sortedPhases.find((p) => p.id === measure.phase);
            const finalStart = parentPhase ? parseDateLocal(parentPhase.startDate) : jan1(MIN_YEAR);
            const finalEnd = parentPhase ? parseDateLocal(parentPhase.endDate) : dec31(MIN_YEAR);
            return {
              id: measure.id,
              text: description,
              start: finalStart,
              end: finalEnd,
              progress: measure.progress || 0,
              parent: measure.phase || 0,
              type: "task",
              measureDisplayIndex: index + 1,
              details: description,
              barDescription: description,
              barMeta,
              categoryLabel,
              subCategoryLabel,
              originalMeasure,
            };
          });
          finalTasks = [...phaseTasks, ...measureTasks];
        } else {
          finalTasks = transformEemToTasks(eemRecords);
        }
        setEemTasks(finalTasks);
        setDirty(false);
      } catch (error) {
        console.error("Failed to load data:", error);
        toast.error(`Unable to load project data: ${error.message}`, { autoClose: 5000 });
        setEemTasks([]);
      } finally {
        setLoading(false);
      }
    },
    [setEemTasks, setLoading]
  );

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Selections, open editors and menus can all point at ids that a save has just replaced, so
  // clear them alongside a refetch. This is the part a page reload used to do for free.
  const resetTransientUi = useCallback(() => {
    setSelectedId(null);
    setEditor(null);
    setDeleteTarget(null);
    setCtxMenu(null);
    setTip(null);
    setPhaseMenuOpen(false);
    setCollapsed({});
  }, []);

  const refreshFromBackend = useCallback(async () => {
    resetTransientUi();
    await loadData();
  }, [loadData, resetTransientUi]);

  // ------------------------------------------------------- derived structure
  const tasks = useMemo(() => (Array.isArray(eemTasks) ? eemTasks : []), [eemTasks]);
  const phaseIds = useMemo(
    () => new Set(tasks.filter((t) => t.isPhase).map((t) => t.id)),
    [tasks]
  );

  const phases = useMemo(() => tasks.filter((t) => t.isPhase), [tasks]);
  const measuresOf = useCallback(
    (phaseId) => tasks.filter((t) => !t.isPhase && t.parent === phaseId),
    [tasks]
  );
  const orphanMeasures = useMemo(
    () => tasks.filter((t) => !t.isPhase && (isRootParent(t.parent) || !phaseIds.has(t.parent))),
    [tasks, phaseIds]
  );

  const categoryOptions = useMemo(() => {
    const set = new Set();
    tasks.forEach((t) => { if (!t.isPhase) { const c = getMeasureCategoryText(t); if (c) set.add(c); } });
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [tasks]);

  const subCategoryOptions = useMemo(() => {
    const set = new Set();
    tasks.forEach((t) => {
      if (t.isPhase) return;
      if (selectedCategory && getMeasureCategoryText(t) !== selectedCategory) return;
      const s = getMeasureSubCategoryText(t);
      if (s) set.add(s);
    });
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [tasks, selectedCategory]);

  useEffect(() => {
    if (selectedSubCategory && !subCategoryOptions.includes(selectedSubCategory)) {
      setSelectedSubCategory("");
    }
  }, [subCategoryOptions, selectedSubCategory]);

  const query = search.trim().toLowerCase();
  const filtersActive = Boolean(
    selectedCategory || selectedSubCategory || selectedMac || selectedPayback
  );

  const measureMatchesFilters = useCallback((m) => {
    if (selectedCategory && getMeasureCategoryText(m) !== selectedCategory) return false;
    if (selectedSubCategory && getMeasureSubCategoryText(m) !== selectedSubCategory) return false;
    if (selectedMac && !bucketMatches(MAC_BUCKETS, selectedMac, getMeasureNumber(m, "mac"))) {
      return false;
    }
    if (
      selectedPayback &&
      !bucketMatches(PAYBACK_BUCKETS, selectedPayback, getMeasureNumber(m, "paybackAfterRebate"))
    ) {
      return false;
    }
    return true;
  }, [selectedCategory, selectedSubCategory, selectedMac, selectedPayback]);

  const measureMatchesSearch = useCallback(
    (m) => (query ? measureSearchBlob(m).includes(query) : false),
    [query]
  );

  // View model: ordered groups (phases + trailing "unassigned"), with filter
  // hiding and search float-to-top prioritization.
  const viewGroups = useMemo(() => {
    const groups = phases.map((phase, index) => {
      let ms = measuresOf(phase.id);
      if (filtersActive) ms = ms.filter(measureMatchesFilters);
      let matched = 0;
      if (query) {
        const hit = [];
        const rest = [];
        ms.forEach((m) => (measureMatchesSearch(m) ? hit.push(m) : rest.push(m)));
        matched = hit.length;
        ms = [...hit, ...rest];
      }
      return { phase, colorIdx: index % PHASE_COLORS.length, measures: ms, matched };
    }).filter((g) => !filtersActive || g.measures.length > 0);

    if (query) {
      groups.sort((a, b) => (b.matched > 0 ? 1 : 0) - (a.matched > 0 ? 1 : 0));
    }

    let orphans = orphanMeasures;
    if (filtersActive) orphans = orphans.filter(measureMatchesFilters);
    if (query) {
      const hit = [];
      const rest = [];
      orphans.forEach((m) => (measureMatchesSearch(m) ? hit.push(m) : rest.push(m)));
      orphans = [...hit, ...rest];
    }
    return { groups, orphans };
  }, [phases, measuresOf, orphanMeasures, filtersActive, measureMatchesFilters, query, measureMatchesSearch]);

  const searchCount = useMemo(() => {
    if (!query) return 0;
    return tasks.filter((t) => !t.isPhase && measureMatchesSearch(t)).length;
  }, [tasks, query, measureMatchesSearch]);

  const selectedTask = useMemo(
    () => tasks.find((t) => t.id === selectedId) || null,
    [tasks, selectedId]
  );

  // ------------------------------------------------------------- mutations
  // Every local mutation funnels through here, so it is the one place that marks unsaved work.
  const commitTasks = useCallback((next) => {
    setEemTasks(next);
    setDirty(true);
  }, [setEemTasks]);

  const updateTask = useCallback((id, patch) => {
    commitTasks(tasks.map((t) => (t.id === id ? { ...t, ...patch } : t)));
  }, [tasks, commitTasks]);

  const setPhaseYears = useCallback((phaseId, startYear, endYear, extraPatch = {}) => {
    const s = clampYear(startYear);
    const e = clampYear(Math.max(startYear, endYear));
    // A new start year can change where the phase sits in the sequence.
    commitTasks(orderPhasesByYear(tasks.map((t) => {
      if (t.id === phaseId) return { ...t, ...extraPatch, start: jan1(s), end: dec31(e) };
      if (!t.isPhase && t.parent === phaseId) return { ...t, start: jan1(s), end: dec31(e) };
      return t;
    })));
  }, [tasks, commitTasks]);

  const movePhase = useCallback((phaseId, direction) => {
    const order = phases.map((p) => p.id);
    const idx = order.indexOf(phaseId);
    const target = idx + direction;
    if (idx < 0 || target < 0 || target >= order.length) return false;
    const newOrder = [...order];
    [newOrder[idx], newOrder[target]] = [newOrder[target], newOrder[idx]];
    const rebuilt = [];
    newOrder.forEach((id) => {
      const phase = phases.find((p) => p.id === id);
      rebuilt.push(phase, ...measuresOf(id));
    });
    rebuilt.push(...orphanMeasures);
    // Only phases that share a start year can swap places; across years the year decides.
    const ordered = orderPhasesByYear(rebuilt);
    if (samePhaseOrder(ordered, tasks)) {
      toast.info("Phases follow their start year. Change the year to move this phase.", { autoClose: 2500 });
      return false;
    }
    commitTasks(ordered);
    return true;
  }, [tasks, phases, measuresOf, orphanMeasures, commitTasks]);

  const reorderPhaseTo = useCallback((phaseId, anchorPhaseId, before) => {
    if (phaseId === anchorPhaseId) return;
    const order = phases.map((p) => p.id).filter((id) => id !== phaseId);
    const anchorIdx = order.indexOf(anchorPhaseId);
    if (anchorIdx < 0) return;
    order.splice(anchorIdx + (before ? 0 : 1), 0, phaseId);
    const rebuilt = [];
    order.forEach((id) => {
      const phase = phases.find((p) => p.id === id);
      rebuilt.push(phase, ...measuresOf(id));
    });
    rebuilt.push(...orphanMeasures);
    // Same rule as the Up/Down buttons: a drop that fights the start years is refused up front
    // rather than silently undone on save.
    const ordered = orderPhasesByYear(rebuilt);
    if (samePhaseOrder(ordered, tasks)) {
      toast.info("Phases follow their start year. Change the year to move this phase.", { autoClose: 2500 });
      return;
    }
    commitTasks(ordered);
  }, [tasks, phases, measuresOf, orphanMeasures, commitTasks]);

  const moveMeasure = useCallback((measureId, direction) => {
    const measure = tasks.find((t) => t.id === measureId);
    if (!measure) return false;
    const siblings = measuresOf(measure.parent);
    const idx = siblings.findIndex((m) => m.id === measureId);
    const target = idx + direction;
    if (idx < 0 || target < 0 || target >= siblings.length) return false;
    const newSiblings = [...siblings];
    [newSiblings[idx], newSiblings[target]] = [newSiblings[target], newSiblings[idx]];
    const rebuilt = [];
    phases.forEach((p) => {
      rebuilt.push(p, ...(p.id === measure.parent ? newSiblings : measuresOf(p.id)));
    });
    rebuilt.push(...(isRootParent(measure.parent) ? newSiblings : orphanMeasures));
    commitTasks(rebuilt);
    return true;
  }, [tasks, phases, measuresOf, orphanMeasures, commitTasks]);

  const reparentMeasure = useCallback((measureId, targetPhaseId, beforeMeasureId = null) => {
    const measure = tasks.find((t) => t.id === measureId);
    const targetPhase = phases.find((p) => p.id === targetPhaseId);
    if (!measure || !targetPhase) return;
    const phaseStartYear = yearOf(targetPhase.start);
    const duration = Math.max(0, yearOf(measure.end) - yearOf(measure.start));
    const moved = {
      ...measure,
      parent: targetPhaseId,
      start: jan1(phaseStartYear),
      end: dec31(clampYear(phaseStartYear + duration)),
    };
    const rebuilt = [];
    phases.forEach((p) => {
      rebuilt.push(p);
      let ms = measuresOf(p.id).filter((m) => m.id !== measureId);
      if (p.id === targetPhaseId) {
        const at = beforeMeasureId ? ms.findIndex((m) => m.id === beforeMeasureId) : -1;
        if (at >= 0) ms.splice(at, 0, moved);
        else ms.push(moved);
      }
      rebuilt.push(...ms);
    });
    rebuilt.push(...orphanMeasures.filter((m) => m.id !== measureId));
    commitTasks(rebuilt);
  }, [tasks, phases, measuresOf, orphanMeasures, commitTasks]);

  // ------------------------------------------------------------ toolbar ops
  const openCreateEditor = () => {
    setEditor({
      mode: "create",
      phaseId: null,
      draft: { name: `Phase ${phases.length + 1}`, startYear: MIN_YEAR, endYear: MIN_YEAR },
    });
  };

  const openEditEditor = (phase) => {
    setEditor({
      mode: "edit",
      phaseId: phase.id,
      draft: {
        name: String(phase.text || ""),
        startYear: yearOf(phase.start),
        endYear: Math.max(yearOf(phase.start), yearOf(phase.end)),
      },
    });
  };

  const handleEditorSave = () => {
    if (!editor) return;
    const { mode, phaseId, draft } = editor;
    const name = draft.name.trim() || "Phase";
    if (mode === "create") {
      const newPhase = {
        id: `temp-phase-${Date.now()}`,
        text: name,
        start: jan1(clampYear(draft.startYear)),
        end: dec31(clampYear(Math.max(draft.startYear, draft.endYear))),
        progress: 0,
        parent: 0,
        type: "task",
        open: true,
        isPhase: true,
      };
      // Slots the new phase by its year rather than appending it at the bottom.
      commitTasks(orderPhasesByYear([...tasks, newPhase]));
      setSelectedId(newPhase.id);
      toast.success("Phase created", { autoClose: 2000 });
    } else {
      setPhaseYears(phaseId, draft.startYear, draft.endYear, { text: name });
      toast.success("Phase saved successfully", { autoClose: 2000 });
    }
    setEditor(null);
  };

  const handleEdit = () => {
    if (selectedTask?.isPhase) openEditEditor(selectedTask);
    else toast.info("Only phases can be edited", { autoClose: 2000 });
  };

  const handleDeleteClick = () => {
    if (!selectedTask) return;
    if (!selectedTask.isPhase) {
      toast.info("You cannot delete a measure", { autoClose: 2000 });
      return;
    }
    setDeleteTarget(selectedTask);
  };

  const confirmDelete = () => {
    const phase = deleteTarget;
    setDeleteTarget(null);
    setEditor(null);
    if (!phase) return;
    // Children become unassigned (root) rather than disappearing.
    commitTasks(
      tasks
        .filter((t) => t.id !== phase.id)
        .map((t) => (!t.isPhase && t.parent === phase.id ? { ...t, parent: 0 } : t))
    );
    if (selectedId === phase.id) setSelectedId(null);
    toast.success("Phase deleted successfully", { autoClose: 2000 });
  };

  const handleMoveUpDown = (direction) => {
    if (!selectedTask) return;
    if (selectedTask.isPhase) movePhase(selectedTask.id, direction);
    else moveMeasure(selectedTask.id, direction);
  };

  // ------------------------------------------------------- context menu
  const openContextMenu = (e, task) => {
    e.preventDefault();
    setSelectedId(task.id);
    setPhaseMenuOpen(false);
    setTip(null);
    setCtxMenu({ x: e.clientX, y: e.clientY, task });
  };

  const ctxCanMove = useCallback((task, dir) => {
    const list = task.isPhase ? phases : measuresOf(task.parent);
    const idx = list.findIndex((t) => t.id === task.id);
    return idx >= 0 && idx + dir >= 0 && idx + dir < list.length;
  }, [phases, measuresOf]);

  // Styled tooltip on truncated names (delegated over the grid, so it costs one
  // pair of listeners rather than one per row).
  const handleGridOver = (e) => {
    const el = e.target.closest?.(".hp-measure-name, .hp-phase-name");
    if (!el) return;
    if (el.scrollWidth <= el.clientWidth + 1) { setTip(null); return; }
    const r = el.getBoundingClientRect();
    setTip({ text: el.textContent, x: r.left, y: r.bottom + 8 });
  };
  const handleGridOut = (e) => {
    if (e.target.closest?.(".hp-measure-name, .hp-phase-name")) setTip(null);
  };

  const handleRearrangePhases = () => {
    if (phases.length < 2) {
      toast.warning("At least 2 phases are required to reorder", { autoClose: 2000 });
      return;
    }
    const sorted = [...phases].sort((a, b) => {
      const ta = new Date(a.start).getTime();
      const tb = new Date(b.start).getTime();
      if (Number.isNaN(ta)) return 1;
      if (Number.isNaN(tb)) return -1;
      return ta - tb;
    });
    const changed =
      sorted.some((p, i) => p.id !== phases[i].id) ||
      sorted.some((p, i) => String(p.text || "") !== `Phase ${i + 1}`);
    if (!changed) {
      toast.info("Phases are already in chronological order", { autoClose: 1800 });
      return;
    }
    const rebuilt = [];
    sorted.forEach((phase, index) => {
      rebuilt.push({ ...phase, text: `Phase ${index + 1}` }, ...measuresOf(phase.id));
    });
    rebuilt.push(...orphanMeasures);
    commitTasks(rebuilt);
    toast.success("Phases rearranged by start year", { autoClose: 2000 });
  };

  const handleRefresh = async () => {
    toast.info("Resetting data", { autoClose: 1200 });
    await refreshFromBackend();
  };

  const buildSavePayload = () => {
    // Always persist the FULL task list, never the filtered/searched view, in start-year order:
    // the backend reads phaseOrder from the array position and simulates phases in that sequence.
    const ordered = orderPhasesByYear(tasks);
    const phaseTasks = ordered.filter((t) => t.isPhase);
    const measureTasks = ordered.filter((t) => !t.isPhase);
    const projectId = getProjectIdFromQuery() || 2167759;
    const payload = transformToBackendFormat(phaseTasks, measureTasks, projectId);

    /*
     * Saving only what is on screen is not enough. The backend has no delete: a phase left out of
     * the payload stays active and comes back on the next load, so regrouping or deleting would
     * quietly accumulate orphans. Post the dropped ones back explicitly as inactive.
     */
    const liveIds = new Set(phaseTasks.map((t) => t.id));
    const asDate = (v) => {
      const d = parseDateLocal(v);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    };
    const retired = (savedPhasesRef.current || [])
      .filter((p) => p && p.id != null && !liveIds.has(p.id))
      .map((p) => ({
        id: p.id,
        phaseName: p.phaseName,
        startDate: asDate(p.startDate),
        endDate: asDate(p.endDate),
        parentProject: projectId,
        phaseOrder: p.phaseOrder || 0,
        measureIds: [],
        active: false,
      }));

    return { projectId, payload: { ...payload, phases: [...payload.phases, ...retired] } };
  };

  const handleSave = async () => {
    try {
      const { projectId, payload } = buildSavePayload();
      toast.info("Saving Gantt structure", { autoClose: 2000 });
      await saveGanttStructure(projectId, payload);
      await refreshFromBackend();
      toast.success("Gantt structure saved", { autoClose: 2000 });
    } catch (error) {
      console.error("Save failed:", error);
      toast.error(`Unable to save: ${error.message}`, { autoClose: 5000 });
    }
  };

  const handleSaveAndRecalculate = async () => {
    try {
      setRecalculating(true);
      const { projectId, payload } = buildSavePayload();
      toast.info("Saving Gantt structure", { autoClose: 2000 });
      await saveGanttStructure(projectId, payload);
      toast.success("Gantt structure saved", { autoClose: 1500 });
      toast.info("Recalculating phased EEM", { autoClose: 2000 });
      await reCalculatePhasedEem(projectId);
      await refreshFromBackend();
      toast.success("Phased EEM recalculated", { autoClose: 2000 });
    } catch (error) {
      console.error("Save and Recalculate failed:", error);
      toast.error(`Operation failed: ${error.message}`, { autoClose: 5000 });
    } finally {
      setRecalculating(false);
    }
  };

  /*
   * Regroup every measure into a fresh set of phases.
   *
   * This replaces the whole phase structure rather than only sweeping up unassigned measures,
   * so the modes are interchangeable: you can group by category, look at it, then group by year
   * without clearing anything first. It is also purely local. Nothing is written until Save, so
   * picking a grouping to look at no longer commits it, and Refresh discards it.
   *
   * Measures are snapped onto their phase's span because that is what the backend hands back on
   * the next load; leaving them on their old years would show a layout that a save would change.
   */
  const regroupMeasures = (groupBy) => {
    const actionLabel =
      groupBy === "category" ? "Grouping measures by category"
        : groupBy === "subcategory" ? "Grouping measures by subcategory"
          : groupBy === "engineeringPriority" ? "Grouping measures by engineering priority"
            : groupBy === "laborIntensity" ? "Grouping measures by labor intensity"
              : "Grouping measures by start year";

    const allMeasures = tasks.filter((t) => !t.isPhase);
    if (allMeasures.length === 0) {
      toast.warning("There are no measures to group");
      return;
    }
    toast.info(actionLabel, { autoClose: 1500 });

    const grouped = {};
    allMeasures.forEach((measure) => {
      const startYear = yearOf(measure.start);
      let key = String(startYear).padStart(4, "0");
      let label = `Phase ${startYear}`;
      if (groupBy === "category") {
        key = label = getMeasureCategoryText(measure) || "Uncategorized";
      } else if (groupBy === "subcategory") {
        key = label = getMeasureSubCategoryText(measure) || "Uncategorized";
      } else if (groupBy === "engineeringPriority") {
        ({ key, label } = ratingGroup(measure, "engineeringPriority", "Priority"));
      } else if (groupBy === "laborIntensity") {
        ({ key, label } = ratingGroup(measure, "laborIntensity", "Labor intensity"));
      }
      if (!grouped[key]) grouped[key] = { label, measures: [] };
      grouped[key].measures.push(measure);
    });

    if (groupBy === "engineeringPriority" || groupBy === "laborIntensity") {
      const rated = Object.keys(grouped).filter((k) => k !== UNRATED_KEY).length;
      if (rated === 0) {
        toast.warning(
          groupBy === "engineeringPriority"
            ? "None of these measures carry an engineering priority rating"
            : "None of these measures carry a labor intensity rating"
        );
        return;
      }
    }

    // Year and rating keys are zero-padded, so a plain string sort orders them correctly and
    // leaves the "Unrated" sentinel last.
    const keys = Object.keys(grouped).sort((a, b) => a.localeCompare(b));

    const stamp = Date.now();
    const next = [];
    keys.forEach((key, index) => {
      const entry = grouped[key];
      const phaseId = `temp-phase-${stamp}-${index}`;
      const starts = entry.measures.map((m) => new Date(m.start).getTime());
      const ends = entry.measures.map((m) => new Date(m.end).getTime());
      const phaseStart = jan1(clampYear(yearOf(new Date(Math.min(...starts)))));
      const phaseEnd = dec31(clampYear(yearOf(new Date(Math.max(...ends)))));
      next.push({
        id: phaseId,
        text: entry.label,
        start: phaseStart,
        end: phaseEnd,
        progress: 0,
        parent: 0,
        type: "task",
        open: true,
        isPhase: true,
      });
      entry.measures.forEach((measure) => {
        next.push({ ...measure, parent: phaseId, start: phaseStart, end: phaseEnd });
      });
    });

    setSelectedId(null);
    setEditor(null);
    setCollapsed({});
    // Group phases take their years from their measures, so they are sequenced by year too;
    // groups that share a year keep the grouping's own order.
    commitTasks(orderPhasesByYear(next));
    toast.success(
      `Regrouped ${allMeasures.length} measures into ${keys.length} phases. Save to keep it.`,
      { autoClose: 4000 }
    );
  };

  // ------------------------------------------------------ dropdown behavior
  useEffect(() => {
    if (!phaseMenuOpen) return undefined;
    const close = (e) => {
      if (!e.target.closest?.(".hp-split")) setPhaseMenuOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setPhaseMenuOpen(false); };
    document.addEventListener("mousedown", close, true);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("mousedown", close, true);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [phaseMenuOpen]);

  // -------------------------------------------------------------- dragging
  // One pointer-drag system for three gestures:
  //  - bar-horizontal: change a task's start year (phase drags children along)
  //  - phase row vertical: reorder phases at root (ghost + insertion line)
  //  - measure row vertical: move a measure between/within phases
  //
  // Document listeners are a single STABLE pair that delegates to the current
  // render's handlers via a ref. Registering per-render closures directly is
  // a trap: an interrupted drag (focus loss, killed event stream) leaves the
  // old closure attached, and every later drop then ALSO fires that stale
  // closure — reordering against an outdated phase list.
  const dragRef = useRef(null);
  const dragHandlersRef = useRef({ move: null, end: null });
  const stableDragMove = useRef((e) => dragHandlersRef.current.move?.(e)).current;
  const stableDragEnd = useRef((e) => dragHandlersRef.current.end?.(e)).current;

  const beginDrag = (e, payload) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    setTip(null);
    dragRef.current = {
      ...payload,
      startX: e.clientX,
      startY: e.clientY,
      moved: false,
      ghost: null,
      indicator: null,
      barEl: payload.barEl || null,
    };
    // Same function identity every time: the browser dedupes repeat adds, so
    // duplicate listeners are structurally impossible.
    document.addEventListener("mousemove", stableDragMove, true);
    document.addEventListener("mouseup", stableDragEnd, true);
  };

  const ensureRowDragVisuals = (d) => {
    if (d.ghost) return;
    document.body.classList.add("hp-dragging");
    d.sourceEl?.classList.add("hp-drag-source");
    const ghost = document.createElement("div");
    ghost.className = "phase-drag-ghost";
    const title = document.createElement("div");
    title.className = "phase-drag-ghost-title";
    title.textContent = d.label;
    ghost.appendChild(title);
    if (d.sub) {
      const meta = document.createElement("div");
      meta.className = "phase-drag-ghost-meta";
      meta.textContent = d.sub;
      ghost.appendChild(meta);
    }
    document.body.appendChild(ghost);
    d.ghost = ghost;
    const indicator = document.createElement("div");
    indicator.className = "phase-drop-indicator";
    document.body.appendChild(indicator);
    d.indicator = indicator;
  };

  const rowDropSlot = (d, clientY) => {
    const rows = [...document.querySelectorAll(".hp-row[data-task-id]")];

    if (d.kind === "phase-row") {
      // Build the visible phase blocks in DOM order (dragged one excluded),
      // then offer the BOUNDARIES between blocks as drop gaps and pick the
      // gap nearest the pointer. Compared to midpoint targeting this is
      // stable: the indicator glides monotonically as the pointer moves.
      const blocks = [];
      rows.forEach((rowEl) => {
        if (rowEl.getAttribute("data-kind") !== "phase") return;
        const blockPhaseId = rowEl.getAttribute("data-phase-id");
        if (String(d.taskId) === blockPhaseId) return;
        const blockRows = rows.filter((r) => r.getAttribute("data-phase-id") === blockPhaseId);
        const top = Math.min(...blockRows.map((r) => r.getBoundingClientRect().top));
        const bottom = Math.max(...blockRows.map((r) => r.getBoundingClientRect().bottom));
        blocks.push({ id: rowEl.getAttribute("data-task-id"), top, bottom });
      });
      if (!blocks.length) return null;
      const gaps = blocks.map((b) => ({ anchorId: b.id, before: true, y: b.top }));
      const last = blocks[blocks.length - 1];
      gaps.push({ anchorId: last.id, before: false, y: last.bottom });
      let best = null;
      gaps.forEach((g) => {
        const dist = Math.abs(clientY - g.y);
        if (!best || dist < best.dist) best = { ...g, dist };
      });
      return best;
    }

    // Measure drag: land inside a phase, before a specific measure or at
    // the end of the phase when over its header row.
    let best = null;
    rows.forEach((rowEl) => {
      const id = rowEl.getAttribute("data-task-id");
      const kind = rowEl.getAttribute("data-kind");
      const phaseIdAttr = rowEl.getAttribute("data-phase-id");
      if (!phaseIdAttr || phaseIdAttr === "orphan") return;
      if (String(d.taskId) === id) return;
      const rect = rowEl.getBoundingClientRect();
      if (clientY < rect.top || clientY > rect.bottom) return;
      if (kind === "phase") {
        best = { phaseIdAttr, beforeMeasureId: null, y: rect.bottom, dist: 0 };
      } else {
        const before = clientY < (rect.top + rect.bottom) / 2;
        best = {
          phaseIdAttr,
          beforeMeasureId: before ? id : null,
          afterMeasureId: before ? null : id,
          y: before ? rect.top : rect.bottom,
          dist: 0,
        };
      }
    });
    return best;
  };

  const onDragMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (!d.moved && Math.abs(dx) < 4 && Math.abs(dy) < 4) return;
    d.moved = true;

    const sc = scrollRef.current;
    const scRect = sc?.getBoundingClientRect();

    if (d.kind === "bar") {
      // Auto-scroll horizontally when dragging a bar near the container edge.
      if (sc && scRect) {
        if (e.clientX > scRect.right - 48) sc.scrollLeft += 16;
        else if (e.clientX < scRect.left + LABEL_W + DATE_W + 24) sc.scrollLeft -= 16;
      }
      const deltaYears = Math.round(dx / COL_W);
      const year = clampYear(d.initialYear + deltaYears);
      d.currentYear = year;
      if (d.barEl) {
        d.barEl.style.transform = `translateX(${(year - d.initialYear) * COL_W}px)`;
        d.barEl.classList.add("hp-bar-dragging");
        d.barEl.title = `Year: ${year}`;
      }
      return;
    }

    // Auto-scroll vertically when a row drag approaches the container edge,
    // so phases below/above the fold are reachable in one gesture.
    if (sc && scRect) {
      if (e.clientY > scRect.bottom - 48) sc.scrollTop += 14;
      else if (e.clientY < scRect.top + 60) sc.scrollTop -= 14;
    }

    ensureRowDragVisuals(d);
    d.ghost.style.transform = `translate3d(${e.clientX + 14}px, ${e.clientY + 12}px, 0)`;
    const slot = rowDropSlot(d, e.clientY);
    // Hide the indicator when its gap sits outside the visible grid area
    // (it is fixed-positioned and would otherwise float over the toolbar).
    const slotVisible =
      slot && scRect && slot.y >= scRect.top - 4 && slot.y <= scRect.bottom + 4;
    if (slot && slotVisible) {
      d.indicator.style.left = `${scRect ? scRect.left : 24}px`;
      d.indicator.style.width = `${scRect ? Math.min(scRect.width, LABEL_W + DATE_W + 500) : 400}px`;
      d.indicator.style.top = `${slot.y - 2}px`;
      d.indicator.classList.add("visible");
      d.lastSlot = slot;
    } else if (d.indicator) {
      d.indicator.classList.remove("visible");
      d.lastSlot = slot || null;
    }
  };

  const onDragEnd = (e) => {
    document.removeEventListener("mousemove", stableDragMove, true);
    document.removeEventListener("mouseup", stableDragEnd, true);
    const d = dragRef.current;
    dragRef.current = null;
    if (!d) return;

    if (d.ghost) d.ghost.remove();
    if (d.indicator) d.indicator.remove();
    d.sourceEl?.classList.remove("hp-drag-source");
    document.body.classList.remove("hp-dragging");
    if (d.barEl) {
      d.barEl.style.transform = "";
      d.barEl.classList.remove("hp-bar-dragging");
    }

    if (!d.moved) {
      setSelectedId(d.taskId);
      return;
    }

    if (d.kind === "bar") {
      if (d.currentYear !== undefined && d.currentYear !== d.initialYear) {
        const task = tasks.find((t) => t.id === d.taskId);
        if (!task) return;
        const duration = Math.max(0, yearOf(task.end) - yearOf(task.start));
        if (task.isPhase) {
          setPhaseYears(task.id, d.currentYear, d.currentYear + duration);
        } else {
          updateTask(task.id, {
            start: jan1(d.currentYear),
            end: dec31(clampYear(d.currentYear + duration)),
          });
        }
      }
      return;
    }

    const slot = d.lastSlot || rowDropSlot(d, e.clientY);
    if (!slot) return;
    if (d.kind === "phase-row") {
      reorderPhaseTo(d.taskId, isNaN(Number(slot.anchorId)) ? slot.anchorId : Number(slot.anchorId), slot.before);
    } else {
      const targetPhaseId = isNaN(Number(slot.phaseIdAttr)) ? slot.phaseIdAttr : Number(slot.phaseIdAttr);
      const before = slot.beforeMeasureId
        ? (isNaN(Number(slot.beforeMeasureId)) ? slot.beforeMeasureId : Number(slot.beforeMeasureId))
        : null;
      reparentMeasure(d.taskId, targetPhaseId, before);
    }
  };

  // Keep the stable document listeners pointing at THIS render's handlers.
  dragHandlersRef.current.move = onDragMove;
  dragHandlersRef.current.end = onDragEnd;

  // Remove stable listeners if the component unmounts mid-drag.
  useEffect(() => () => {
    document.removeEventListener("mousemove", stableDragMove, true);
    document.removeEventListener("mouseup", stableDragEnd, true);
    document.body.classList.remove("hp-dragging");
  }, [stableDragMove, stableDragEnd]);

  // ------------------------------------------------------------- rendering
  const timelineW = YEARS.length * COL_W;
  const totalW = LABEL_W + DATE_W + timelineW;

  // Previous UI: bars are small position-only pills at the start year, with
  // the label rendered outside to the right (phase: one bold line; measure:
  // name + "Category | Subcategory" second line). The pill sits at the LEFT
  // edge of its start-year column (the .hp-bar's 4px padding gives a small
  // inset), so a bar visually begins at the start of that year.
  const renderBar = (task, colorIdx, isPhase) => {
    const startYear = yearOf(task.start);
    const left = (startYear - MIN_YEAR) * COL_W;
    const category = !isPhase ? getMeasureCategoryText(task) : "";
    const subCategory = !isPhase ? getMeasureSubCategoryText(task) : "";
    const meta = [category, subCategory].filter(Boolean).join("  |  ");
    return (
      <div
        className={isPhase ? "hp-bar hp-bar-phase" : "hp-bar hp-bar-measure"}
        style={{ left }}
        onMouseDown={(e) =>
          beginDrag(e, {
            kind: "bar",
            taskId: task.id,
            initialYear: startYear,
            barEl: e.currentTarget,
          })
        }
        title={`Year: ${startYear}`}
      >
        <span className="hp-bar-pill" />
        <span className="hp-bar-labelblock">
          <span className="hp-bar-label">
            <Highlight text={String(task.text || "")} query={!isPhase ? search.trim() : ""} />
          </span>
          {!isPhase && meta && <span className="hp-bar-meta">{meta}</span>}
        </span>
      </div>
    );
  };

  const renderYearCells = (shade) => (
    <div className="hp-yearcells" style={{ width: timelineW }}>
      {YEARS.map((y, i) => (
        <div
          key={y}
          className={`hp-yearcell ${i % 2 === 1 ? "alt" : ""} ${shade ? "shade" : ""}`}
          style={{ width: COL_W }}
        />
      ))}
    </div>
  );

  if (loading) {
    return (
      <div className="hp-root hp-loading">
        <div className="hp-spinner" />
        <p>Loading EEM measures…</p>
      </div>
    );
  }

  const { groups, orphans } = viewGroups;

  return (
    <div className="hp-root">
      <div className="hp-card">
        {/* ------------------------------------------------------ toolbar */}
        <div className="hp-toolbar">
          <div className="hp-toolbar-left">
            <button className="hp-tool" onClick={openCreateEditor} title="Add phase">
              <HpIcon name="add" />Add
            </button>
            <button className="hp-tool" onClick={handleDeleteClick} title="Delete phase">
              <HpIcon name="delete" />Delete
            </button>
            <button className="hp-tool" onClick={handleEdit} title="Edit phase">
              <HpIcon name="edit" />Edit
            </button>
            <span className="hp-tool-sep" />
            <button
              className="hp-tool"
              onClick={() => toast.info("A phase cannot be nested inside another phase", { autoClose: 2000 })}
              title="Indent"
            >
              <HpIcon name="indent" />Indent
            </button>
            <button className="hp-tool" onClick={() => handleMoveUpDown(-1)} title="Move up">
              <HpIcon name="up" />Up
            </button>
            <button className="hp-tool" onClick={() => handleMoveUpDown(1)} title="Move down">
              <HpIcon name="down" />Down
            </button>
            <button className="hp-tool" onClick={handleRearrangePhases} title="Reorder phases by start year">
              <HpIcon name="reorder" />Reorder
            </button>
            <span className="hp-tool-sep" />
            <button
              className={`hp-tool ${showFilters ? "active" : ""}`}
              onClick={() => setShowFilters((v) => !v)}
              title="Search & filters"
            >
              <HpIcon name="filters" />Filters
            </button>
            <button className="hp-tool" onClick={handleRefresh} title="Reload data">
              <HpIcon name="refresh" />Refresh
            </button>
          </div>
          <div className="hp-toolbar-right">
            <div className="hp-split">
              <button className="hp-btn hp-btn-outline" onClick={() => setPhaseMenuOpen((v) => !v)}>
                <HpIcon name="sparkle" className="hp-btn-ico" /> Create Phase
                <span className={`hp-caret ${phaseMenuOpen ? "open" : ""}`}>▾</span>
              </button>
              {phaseMenuOpen && (
                <div className="hp-menu">
                  <div className="hp-menu-title">Create Phase</div>
                  <div className="hp-menu-note">Regroups every measure and replaces the current phases. Nothing is saved until you press Save.</div>
                  <button className="hp-menu-item" onClick={() => { setPhaseMenuOpen(false); regroupMeasures("year"); }}>
                    <strong>Auto-create Phases</strong>
                    <span>One phase per start year</span>
                  </button>
                  <button className="hp-menu-item" onClick={() => { setPhaseMenuOpen(false); regroupMeasures("category"); }}>
                    <strong>Group by Category</strong>
                    <span>One phase per measure category</span>
                  </button>
                  <button className="hp-menu-item" onClick={() => { setPhaseMenuOpen(false); regroupMeasures("subcategory"); }}>
                    <strong>Group by Subcategory</strong>
                    <span>One phase per measure subcategory</span>
                  </button>
                  <button className="hp-menu-item" onClick={() => { setPhaseMenuOpen(false); regroupMeasures("engineeringPriority"); }}>
                    <strong>Group by Engineering Priority</strong>
                    <span>Most urgent engineering work first</span>
                  </button>
                  <button className="hp-menu-item" onClick={() => { setPhaseMenuOpen(false); regroupMeasures("laborIntensity"); }}>
                    <strong>Group by Labor Intensity</strong>
                    <span>Least disruptive work first</span>
                  </button>
                </div>
              )}
            </div>
            <button
              className="hp-btn hp-btn-primary"
              onClick={handleSave}
              title={dirty ? "You have unsaved changes" : "Save the current plan"}
            >
              <HpIcon name="save" className="hp-btn-ico" /> {dirty ? "Save *" : "Save"}
            </button>
            <button
              className="hp-btn hp-btn-accent"
              onClick={handleSaveAndRecalculate}
              disabled={recalculating}
            >
              {recalculating ? "Processing…" : (<><HpIcon name="refresh" className="hp-btn-ico" /> Save &amp; Recalculate</>)}
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="hp-filterbar">
            <div className="hp-search">
              <span className="hp-search-icon">🔍</span>
              <input
                ref={searchInputRef}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search measures..."
                className={query && searchCount === 0 ? "no-match" : ""}
              />
              {search && (
                <button
                  className="hp-search-clear"
                  onClick={() => { setSearch(""); searchInputRef.current?.focus(); }}
                >✕</button>
              )}
            </div>
            {query && (
              <span className={`hp-search-pill ${searchCount === 0 ? "none" : ""}`}>
                {searchCount === 0 ? "No matches found" : `${searchCount} measure${searchCount === 1 ? "" : "s"} found`}
              </span>
            )}
            <select
              value={selectedCategory}
              onChange={(e) => { setSelectedCategory(e.target.value); if (!e.target.value) setSelectedSubCategory(""); }}
            >
              <option value="">All categories</option>
              {categoryOptions.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select
              value={selectedSubCategory}
              onChange={(e) => setSelectedSubCategory(e.target.value)}
              disabled={subCategoryOptions.length === 0}
            >
              <option value="">All subcategories</option>
              {subCategoryOptions.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <select
              value={selectedMac}
              onChange={(e) => setSelectedMac(e.target.value)}
              title="Marginal abatement cost, $/tCO2e"
            >
              <option value="">All MAC</option>
              {MAC_BUCKETS.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
            </select>
            <select
              value={selectedPayback}
              onChange={(e) => setSelectedPayback(e.target.value)}
              title="Simple payback after rebate, in years"
            >
              <option value="">All paybacks</option>
              {PAYBACK_BUCKETS.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
            </select>
            {filtersActive && (
              <button
                className="hp-filter-clear"
                onClick={() => {
                  setSelectedCategory("");
                  setSelectedSubCategory("");
                  setSelectedMac("");
                  setSelectedPayback("");
                }}
              >
                Clear filters
              </button>
            )}
          </div>
        )}

        {/* --------------------------------------------------------- grid */}
        <div className="hp-scroll" ref={scrollRef} onMouseOver={handleGridOver} onMouseOut={handleGridOut}>
          <div style={{ width: totalW, minWidth: "100%" }}>
            {/* header */}
            <div className="hp-header hp-rowline">
              <div className="hp-cell hp-cell-name hp-sticky-name">Phase Name</div>
              <div className="hp-cell hp-cell-date hp-sticky-date">Start Date</div>
              <div className="hp-yearhead" style={{ width: timelineW }}>
                {YEARS.map((y, i) => (
                  <div key={y} className={`hp-yearhead-cell ${i % 2 === 1 ? "alt" : ""}`} style={{ width: COL_W }}>
                    {y}
                  </div>
                ))}
              </div>
            </div>

            {groups.map(({ phase, colorIdx, measures }) => {
              const pc = PHASE_COLORS[colorIdx % PHASE_COLORS.length];
              const isCollapsed = collapsed[phase.id];
              const isSelected = selectedId === phase.id;
              return (
                <div key={phase.id} className="hp-phase-block">
                  <div
                    className={`hp-row hp-rowline hp-phase-row ${isSelected ? "selected" : ""}`}
                    data-task-id={phase.id}
                    data-kind="phase"
                    data-phase-id={phase.id}
                    style={{ "--pc": pc.bg, "--pc-light": pc.light }}
                    onMouseDown={(e) => {
                      if (e.target.closest(".hp-collapse, .hp-bar")) return;
                      beginDrag(e, {
                        kind: "phase-row",
                        taskId: phase.id,
                        label: String(phase.text || "Phase"),
                        sub: `${measures.length} measure${measures.length === 1 ? "" : "s"}`,
                        sourceEl: e.currentTarget,
                      });
                    }}
                    onDoubleClick={() => openEditEditor(phase)}
                    onContextMenu={(e) => openContextMenu(e, phase)}
                  >
                    <div className="hp-cell hp-cell-name hp-sticky-name">
                      <button
                        className="hp-collapse"
                        onClick={(e) => {
                          e.stopPropagation();
                          setCollapsed((c) => ({ ...c, [phase.id]: !c[phase.id] }));
                        }}
                      >
                        <span className={`hp-collapse-arrow ${isCollapsed ? "closed" : ""}`}>▼</span>
                      </button>
                      <span className="hp-phase-name">
                        <Highlight text={String(phase.text || "")} query={search.trim()} />
                      </span>
                    </div>
                    <div className="hp-cell hp-cell-date hp-sticky-date">{yearOf(phase.start)}</div>
                    <div className="hp-timeline">
                      {renderYearCells(true)}
                      {renderBar(phase, colorIdx, true)}
                    </div>
                  </div>

                  {!isCollapsed && measures.map((m) => {
                    const mSelected = selectedId === m.id;
                    const matched = query && measureMatchesSearch(m);
                    return (
                      <div
                        key={m.id}
                        className={`hp-row hp-rowline hp-measure-row ${mSelected ? "selected" : ""} ${matched ? "matched" : ""}`}
                        data-task-id={m.id}
                        data-kind="measure"
                        data-phase-id={phase.id}
                        style={{ "--pc": pc.bg }}
                        onMouseDown={(e) => {
                          if (e.target.closest(".hp-bar")) return;
                          beginDrag(e, {
                            kind: "measure-row",
                            taskId: m.id,
                            label: String(m.text || "Measure"),
                            sub: [getMeasureCategoryText(m), getMeasureSubCategoryText(m)].filter(Boolean).join(" | "),
                            sourceEl: e.currentTarget,
                          });
                        }}
                        onContextMenu={(e) => openContextMenu(e, m)}
                      >
                        <div className="hp-cell hp-cell-name hp-sticky-name">
                          <span className="hp-measure-name">
                            <Highlight text={String(m.text || "")} query={search.trim()} />
                          </span>
                        </div>
                        <div className="hp-cell hp-cell-date hp-sticky-date muted">{yearOf(m.start)}</div>
                        <div className="hp-timeline">
                          {renderYearCells(false)}
                          {renderBar(m, colorIdx, false)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}

            {orphans.length > 0 && (
              <div className="hp-phase-block">
                <div className="hp-row hp-rowline hp-orphan-head" data-kind="orphan-head" data-phase-id="orphan">
                  <div className="hp-cell hp-cell-name hp-sticky-name">
                    <span className="hp-orphan-title">Unassigned measures</span>
                    <span className="hp-count neutral">{orphans.length}</span>
                  </div>
                  <div className="hp-cell hp-cell-date hp-sticky-date" />
                  <div className="hp-timeline">{renderYearCells(true)}</div>
                </div>
                {orphans.map((m) => {
                  const mSelected = selectedId === m.id;
                  return (
                    <div
                      key={m.id}
                      className={`hp-row hp-rowline hp-measure-row ${mSelected ? "selected" : ""}`}
                      data-task-id={m.id}
                      data-kind="measure"
                      data-phase-id="orphan"
                      onMouseDown={(e) => {
                        if (e.target.closest(".hp-bar")) return;
                        beginDrag(e, {
                          kind: "measure-row",
                          taskId: m.id,
                          label: String(m.text || "Measure"),
                          sub: [getMeasureCategoryText(m), getMeasureSubCategoryText(m)].filter(Boolean).join(" | "),
                          sourceEl: e.currentTarget,
                        });
                      }}
                      onContextMenu={(e) => openContextMenu(e, m)}
                    >
                      <div className="hp-cell hp-cell-name hp-sticky-name">
                        <span className="hp-measure-name">
                          <Highlight text={String(m.text || "")} query={search.trim()} />
                        </span>
                      </div>
                      <div className="hp-cell hp-cell-date hp-sticky-date muted">{yearOf(m.start)}</div>
                      <div className="hp-timeline">
                        {renderYearCells(false)}
                        {renderBar(m, 0, false)}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {groups.length === 0 && orphans.length === 0 && (
              <div className="hp-empty">
                <div className="hp-empty-icon">📋</div>
                No phases or measures to show.
              </div>
            )}
          </div>
        </div>
      </div>

      {editor && (
        <PhaseEditor
          mode={editor.mode}
          draft={editor.draft}
          onChange={(draft) => setEditor((ed) => ({ ...ed, draft }))}
          onSave={handleEditorSave}
          onCancel={() => setEditor(null)}
          onDelete={() => {
            const phase = tasks.find((t) => t.id === editor.phaseId);
            if (phase) setDeleteTarget(phase);
          }}
        />
      )}

      {deleteTarget && (
        <DeleteModal
          phase={deleteTarget}
          measureNames={measuresOf(deleteTarget.id).map((m) => String(m.text || ""))}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}

      {ctxMenu && (
        <ContextMenu
          x={ctxMenu.x}
          y={ctxMenu.y}
          task={ctxMenu.task}
          canMoveUp={ctxCanMove(ctxMenu.task, -1)}
          canMoveDown={ctxCanMove(ctxMenu.task, 1)}
          onEdit={() => openEditEditor(ctxMenu.task)}
          onMove={(dir) => {
            const t = ctxMenu.task;
            if (t.isPhase) movePhase(t.id, dir);
            else moveMeasure(t.id, dir);
          }}
          onDelete={() => setDeleteTarget(ctxMenu.task)}
          onClose={() => setCtxMenu(null)}
        />
      )}

      {tip && createPortal(
        <div className="hp-tip" style={{ left: tip.x, top: tip.y }}>{tip.text}</div>,
        document.body
      )}
    </div>
  );
}
